import React from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  EdgeProps,
} from '@xyflow/react';
import { Box, Typography, useTheme } from '@mui/material';

export const CustomEdge: React.FC<EdgeProps> = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
}) => {
  const theme = useTheme();

  // Determine curvature based on connection type
  const isBranchConnection =
    data?.label && typeof data.label === 'string' && data.label.trim() !== '';
  const isErrorEdge = data?.isError;
  const isDefaultEdge = data?.isDefault;

  // Use minimal curvature for straight-looking connections, higher for branches/errors
  const curvature =
    isBranchConnection || isErrorEdge || isDefaultEdge ? 0.25 : 0.05;

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    curvature,
  });

  // Determine edge style based on type
  const getEdgeStyle = () => {
    if (isErrorEdge) {
      return {
        stroke: theme.palette.error.main,
        strokeWidth: 2,
        strokeDasharray: '5,5',
      };
    } else if (isDefaultEdge) {
      return {
        stroke: theme.palette.warning.main,
        strokeWidth: 2,
        strokeDasharray: '3,3',
      };
    } else {
      return {
        stroke: theme.palette.grey[400],
        strokeWidth: 2,
        strokeDasharray: 'none',
      };
    }
  };

  const edgeStyle = getEdgeStyle();

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          ...edgeStyle,
        }}
      />
      {data?.label && (
        <EdgeLabelRenderer>
          <Box
            sx={{
              position: 'absolute',
              transform: 'translate(-50%, -50%)',
              fontSize: 12,
              pointerEvents: 'all',
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.grey[300]}`,
              borderRadius: 1,
              padding: '2px 6px',
              boxShadow: theme.shadows[1],
            }}
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.7rem',
                fontWeight: 500,
              }}
            >
              {/* @ts-expect-error */}
              {data?.label}
            </Typography>
          </Box>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export const getEdgeTypes = () => ({
  custom: CustomEdge,
});
